import { createContext, Dispatch, SetStateAction, useContext, useMemo } from "react"

import type {
    SelectedMainUserState,
    CreateOrderDialogFlow,
    CreateOrderDialogType,
    AvailableModules,
} from "../createOrderTypes"
import type { OutlookBookingSuccessArgs } from "../../../../modules/order/pages/outlookCreateOrderTypes"
import type { OrderContextState } from "./helpers"

export type CreateOrderFormSetOrderValue = Readonly<{
    setOrderContext: Dispatch<SetStateAction<OrderContextState>>
    /** Switches the dialog to another existing order and remounts the form subtree for it. */
    switchToOrder: (orderId: number) => void
}>

export type CreateOrderFormConfigValue = Readonly<{
    user: SelectedMainUserState
    dialogType: CreateOrderDialogType
    flowConfig?: CreateOrderDialogFlow
    availableModules: AvailableModules
    handleClose: () => void
    handleOutlookBookingCompleted?: (args: OutlookBookingSuccessArgs) => void
}>

export type CreateOrderFormStateValue = Readonly<{
    orderContext: OrderContextState
    setOrderContext: Dispatch<SetStateAction<OrderContextState>>
}>

const CreateOrderFormConfigContext = createContext<CreateOrderFormConfigValue | null>(null)
const CreateOrderFormStateContext = createContext<CreateOrderFormStateValue | null>(null)
const CreateOrderFormSetOrderContext = createContext<CreateOrderFormSetOrderValue | null>(null)

export function useCreateOrderFormConfig(): CreateOrderFormConfigValue {
    const value = useContext(CreateOrderFormConfigContext)
    if (!value) {
        throw new Error("useCreateOrderFormConfig must be used within CreateOrderFormProvider")
    }
    return value
}

export function useCreateOrderFormState(): CreateOrderFormStateValue {
    const value = useContext(CreateOrderFormStateContext)
    if (!value) {
        throw new Error("useCreateOrderFormState must be used within CreateOrderFormProvider")
    }
    return value
}

export function useCreateOrderFormSetOrder(): CreateOrderFormSetOrderValue {
    const value = useContext(CreateOrderFormSetOrderContext)
    if (!value) {
        throw new Error("useCreateOrderFormSetOrder must be used within CreateOrderFormProvider")
    }
    return value
}

export function useCreateOrderForm(): CreateOrderFormConfigValue & CreateOrderFormStateValue {
    return {
        ...useCreateOrderFormConfig(),
        ...useCreateOrderFormState(),
    }
}

type CreateOrderFormProviderProps = Readonly<{
    children: React.ReactNode
    config: CreateOrderFormConfigValue
    state: CreateOrderFormStateValue
    switchToOrder: (orderId: number) => void
}>

export function CreateOrderFormProvider({ children, config, state, switchToOrder }: CreateOrderFormProviderProps) {
    const setOrderValue = useMemo(
        () => ({ setOrderContext: state.setOrderContext, switchToOrder }),
        [state.setOrderContext, switchToOrder],
    )

    return (
        <CreateOrderFormConfigContext.Provider value={config}>
            <CreateOrderFormSetOrderContext.Provider value={setOrderValue}>
                <CreateOrderFormStateContext.Provider value={state}>{children}</CreateOrderFormStateContext.Provider>
            </CreateOrderFormSetOrderContext.Provider>
        </CreateOrderFormConfigContext.Provider>
    )
}
